import { Clock, ArrowRightLeft, FileText, PackageX, Trophy } from 'lucide-react';
import { canViewHourlyReport } from '@/utils/operationAccess';

// URL sectionId -> backend module key, display name and (if tracked) hourly-report module.
export const SECTION_MODULES = {
  greige: { key: 'greige', name: 'Greige Section' },
  'greige-storage': { key: 'greige_storage', name: 'Greige Storage' },
  fob: { key: 'fob', name: 'FOB Section', hourly: 'FOB' },
  inhouse: { key: 'inhouse', name: 'Inhouse Section', hourly: 'Inhouse' },
  processing: { key: 'processing', name: 'Processing Section', hourly: 'Processing' },
  'goods-transfer': { key: 'goods_transfer', name: 'Goods Transfer', hourly: 'Goods Transfer' },
  dispatch: { key: 'dispatch', name: 'Dispatch Section', hourly: 'Dispatch' },
  unassorted: { key: 'unassorted', name: 'Unassorted Section' },
  tissue: { key: 'tissue', name: 'Tissue Section' },
  flexi: { key: 'flexi', name: 'Flexi Section' },
  'shirt-manufacturing': { key: 'shirt_manufacturing', name: 'Shirt Manufacturing' },
  sample: { key: 'sample', name: 'Sample Section' },
  'sales-return': { key: 'sales_return', name: 'Sales Return Section' },
  accounts: { key: 'accounts', name: 'Accounts Section' },
  trims: { key: 'trims', name: 'Trims Section' },
  'house-keeping': { key: 'house_keeping', name: 'House Keeping' },
  'electrical-goods': { key: 'electrical_goods', name: 'Electrical Goods' },
  'it-goods': { key: 'it_goods', name: 'IT Goods' },
  rnm: { key: 'rnm', name: 'R & M' },
  ecom: { key: 'ecom', name: 'ECOM Section' },
  yarn: { key: 'yarn', name: 'Yarn Section' },
  'admin-section': { key: 'admin_section', name: 'Admin Section' },
  weaving: { key: 'weaving', name: 'Weaving Section' },
};

const tile = (id, title, description, icon, color, route, extra = {}) => ({
  id, title, description, icon, color, iconBg: color, route, testId: `${id}-tile`, ...extra,
});

export const hourlyReportTile = (sectionId) => tile(
  'hourly-report', 'Hourly Report',
  `Pieces per staff by hour — ${SECTION_MODULES[sectionId].hourly} operations only`,
  Clock, 'bg-violet-600', `/sections/${sectionId}/hourly-report`, { alwaysVisible: true },
);

export const opRankingsTile = (sectionId) => tile(
  'operation-rankings', 'Staff Rankings',
  `Monthly ranking per ${SECTION_MODULES[sectionId].hourly} operation — repeat low performers`,
  Trophy, 'bg-indigo-600', `/sections/${sectionId}/operation-rankings`, { alwaysVisible: true },
);

export const itOutReportTile = (sectionId, id = 'internal-transfer-report') => tile(
  id, 'Internal Transfer Report', 'Transfers out of this section — qty, ERP entry no & print sheets',
  ArrowRightLeft, 'bg-purple-600', `/sections/${sectionId}/it-report`,
);

export const documentsReportTile = (sectionId) => tile(
  'documents-report', 'Documents Report', 'Documents received — completed once every bundle has left the section',
  FileText, 'bg-emerald-600', `/sections/${sectionId}/documents-report`,
);

export const fobPackedNotTransferredTile = () => tile(
  'packed-not-transferred-report', 'Packed – Not Transferred',
  'Bundles that finished Assortment Packing but have no Internal Transfer yet',
  PackageX, 'bg-rose-600', '/sections/fob/packed-not-transferred',
);

// Report tiles for a module dashboard; the hourly tile only for permitted users.
export const moduleReportTiles = (sectionId, { it = true, docs = true, itId } = {}) => [
  ...(SECTION_MODULES[sectionId]?.hourly && canViewHourlyReport() ? [hourlyReportTile(sectionId), opRankingsTile(sectionId)] : []),
  ...(it ? [itOutReportTile(sectionId, itId)] : []),
  ...(docs ? [documentsReportTile(sectionId)] : []),
];
